import React from "react";
import { Film } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import { TrendingUp, TrendingDown } from "lucide-react";
import { formatInteger, formatSignedPercent } from "@/utils/numberFormat";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import type { InstagramReelKpi } from "./instagram.types";

/** Os números de Reels saem da conta do Instagram escolhida para a loja. */
const FONTES_DOS_REELS: readonly RequisitoDeFonte[] = ["instagram"];

/** O KPI como a tela o recebe: `value` nulo quando a métrica não veio, para sair N/D e não 0. */
export type KpiDeReel = Omit<InstagramReelKpi, "value"> & {
  value: number | null;
};

interface InstagramReelsSectionProps {
  kpis: readonly KpiDeReel[];
  loading?: boolean;
  /** A busca das métricas falhou. */
  erro?: boolean;
}

/** Inline trend indicator (does NOT depend on comparison filter for mock data) */
function ReelTrend({
  value,
  trend,
}: {
  value: number;
  trend: "up" | "down" | "neutral";
}): React.ReactElement | null {
  if (trend === "neutral" || value === 0) return null;
  const Icon = trend === "up" ? TrendingUp : TrendingDown;
  const color = trend === "up" ? "text-green-500" : "text-red-500";
  return (
    <div className={`flex items-center gap-1 text-sm ${color}`}>
      <Icon className="w-4 h-4" />
      <span>{formatSignedPercent(value, 1)}</span>
    </div>
  );
}

/** Skeleton for the Reels section */
function ReelsSectionSkeleton(): React.ReactElement {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Film className="h-5 w-5 text-muted-foreground" />
        <Skeleton className="h-6 w-16" />
      </div>
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        {[...Array(3)].map((_, i) => (
          <Card key={`reel-sk-main-${i}`} className="card-kpi-primary">
            <CardHeader>
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={`reel-sk-sec-${i}`} className="card-kpi-secondary">
            <CardHeader>
              <Skeleton className="h-4 w-28" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Render a single reel KPI card */
function ReelKpiCard({
  kpi,
  variant,
  erro,
}: {
  kpi: KpiDeReel;
  variant: "primary" | "secondary";
  erro: boolean;
}): React.ReactElement {
  return (
    <Card
      className={
        variant === "primary" ? "card-kpi-primary" : "card-kpi-secondary"
      }
    >
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-1">
            <span className="truncate">{kpi.title}</span>
            <HelpTooltip
              content={kpi.title}
              triggerClassName="h-3.5 w-3.5 text-[9px]"
            />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start min-w-0">
          <EstadoDoValor
            carregando={false}
            disponivel={kpi.value !== null}
            fontes={FONTES_DOS_REELS}
            erro={erro}
            className="text-2xl font-light text-muted-foreground"
          >
            <KpiNumber
              value={kpi.value ?? 0}
              format={(n: number) => formatInteger(n)}
              className="font-light text-foreground"
              maxFontPx={variant === "primary" ? 30 : 28}
              breathingRoomPx={18}
              ariaLabel={kpi.title}
            />
          </EstadoDoValor>
          <ReelTrend value={kpi.change} trend={kpi.trend} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function InstagramReelsSection({
  kpis,
  loading = false,
  erro = false,
}: InstagramReelsSectionProps): React.ReactElement {
  if (loading) return <ReelsSectionSkeleton />;

  const mainKpis = kpis.slice(0, 3);
  const secondaryKpis = kpis.slice(3);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Film className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Reels</h2>
      </div>

      {/* Primary row: 3 KPIs */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        {mainKpis.map((kpi) => (
          <ReelKpiCard key={kpi.key} kpi={kpi} variant="primary" erro={erro} />
        ))}
      </div>

      {/* Secondary row: 4 KPIs */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {secondaryKpis.map((kpi) => (
          <ReelKpiCard
            key={kpi.key}
            kpi={kpi}
            variant="secondary"
            erro={erro}
          />
        ))}
      </div>
    </div>
  );
}
